import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { updateBookForFreeMode } from '../../../pages/Builder/services/builderHelpers';
import { ModalProps } from '../../../components/Common/modal/Modal.interface';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClose } from '@fortawesome/free-solid-svg-icons';

const Modal = ({
  title = '',
  description,
  doProcess,
  submitButtonTitle = 'Ok',
  showTopRightCloseButton = false,
  showCloseButton = true,
  showPromoFreeBook = false,
  setImageGenerationStarted,
  characterRegenerate = false,
  continueBook,
  showCloseButtonFunction,
  showFreeBookViaRelaxMode = false,
  showSubmitButton = true,
  focus = true,
  children, // Accept children for dynamic React components
  className,
  descriptionClassName,
  descriptionClassContainer,
  mainContainerClassName,
  mainTitleContainerClassName = 'main-title-modal-text',
}: ModalProps) => {
  const [modalOpen, setModalOpen] = useState<boolean>(true);
  const [showSwitchButton, setShowSwitchButton] = useState<boolean>(false);
  const user = useSelector((state: RootState) => state.user.user);
  const bookId = useSelector((state: RootState) => state.app.bookId);

  const modalRef = useRef<HTMLDivElement | null>(null);

  // Set focus to the modal when it is rendered

  useEffect(() => {
    if (modalRef.current) {
      modalRef.current.focus();
    }
    setShowSwitchButton(!!user?.plan?.maintenanceMode);
  }, []);

  // Handlers for button actions
  const handleFreeModeContinue = () => {
    if (bookId) {
      updateBookForFreeMode(bookId);
      setImageGenerationStarted && setImageGenerationStarted(false);
      setModalOpen(false);
    }
  };

  const handleClose = () => {
    setModalOpen(false);
    if (showCloseButtonFunction) {
      showCloseButtonFunction();
    }
  };

  const handleRelaxModeContinue = () => {
    if (bookId) {
      // handleContinueBookInFreeMode(bookId);
      setModalOpen(false);
    }
  };

  const modalContent = (
    <div
      className={`fixed inset-0 z-50 ${modalOpen ? 'block' : 'hidden'} ${className}`}
      aria-labelledby="modal-title"
      role="dialog"
      aria-modal="true"
      ref={modalRef}
    >
      <div className="fixed inset-0 bg-gray-800 bg-opacity-75 transition-opacity"></div>
      <div className="fixed inset-0 z-10 w-full overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-8 md:p-4 text-center">
          <div
            className={`relative transform overflow-hidden  rounded-lg bg-storytan px-4 lg:px-8 pb-4 pt-5 text-left shadow-xl transition-all  sm:max-w-6xl main-modal-content-container ${mainContainerClassName}`}
          >
            {showTopRightCloseButton && (
              <FontAwesomeIcon
                icon={faClose}
                className="text-xl text-storyblue p-2 mr-4 sm:mr-5.5 lg:mr-7 mt-3.5 z-10"
                style={{
                  position: 'absolute',
                  top: '0',
                  right: '0',
                  cursor: 'pointer',
                }}
                onClick={handleClose}
              />
            )}

            {/* Content of the modal */}
            {title && (
              <h2
                className={`text-left text-2xl md:text-2xl mb-4 font-bold text-storyblue pr-6 md:pr-4 ${mainTitleContainerClassName}`}
              >
                {title}
              </h2>
            )}
            <div
              className={`grid grid-cols-1 w-full md:w-[500px] md:gap-6 text-justify modal-description ${descriptionClassContainer}`}
            >
              <div style={{ display: 'block' }}>
                <h3
                  className={`${title.length > 1 ? 'text-sm' : 'text-md'} text-gray-500 font-bold pb-1 ${descriptionClassName}`}
                >
                  {description}
                </h3>
              </div>
            </div>

            {/* Dynamic Children */}
            <div className="w-full overflow-y-auto">{children}</div>

            <div className="w-full flex justify-end gap-3 mt-1">
              {showPromoFreeBook && !showSwitchButton && !characterRegenerate && (
                <button
                  className="capitalize flex items-center justify-center rounded-2xl border border-transparent bg-storyblueSecondLightText px-4 py-2 text-base text-white shadow-sm"
                  onClick={handleFreeModeContinue}
                >
                  Continue in Free Mode
                </button>
              )}
              {showSubmitButton && (
                <button
                  className="capitalize flex items-center justify-center rounded-md border border-transparent bg-storyblue px-4 py-2 text-base   text-white shadow-sm"
                  onClick={doProcess}
                  style={{ display: 'block' }}
                >
                  {submitButtonTitle}
                </button>
              )}
              {showCloseButton && (
                <button
                  className="capitalize flex items-center justify-center rounded-md border border-transparent bg-storyblueSecondLightText px-4 py-2 text-base   text-white shadow-sm"
                  onClick={handleClose}
                >
                  Close
                </button>
              )}
              {showFreeBookViaRelaxMode && (
                <button
                  className="capitalize flex items-center justify-center rounded-md border border-transparent bg-storyblueSecondLightText px-4 py-2 text-base   text-white shadow-sm"
                  onClick={handleRelaxModeContinue}
                  autoFocus={true}
                >
                  Continue
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};
export default Modal;
